import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { handleError, validateNonEmpty, validateItemSelected, fetchForInput, fetchOwners, removeNullsAndEmptys, isEditing } from "../../utils/lib";
import { TextField, Autocomplete, Checkbox, FormControlLabel, Menu, MenuItem, ListItemIcon, ListItemText, Avatar, Box } from "@mui/material";
import { PhotoCamera, PhotoLibrary, Delete } from "@mui/icons-material";
import { uploadPhotoAndThumbnail } from "../../utils/fileUploadHelper";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import { useConfig } from '../../providers/ConfigProvider';
import { useTranslation } from 'react-i18next';
import EditPage from "./EditPage";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import usePetStore from "../../store/petStore";
import { v4 as uuidv4 } from 'uuid';
import InlinePhotoCapture from "../../components/InlinePhotoCapture";

const EditPet = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {updateTitle, toggleFab} = useAppBar();
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const {config, isLoadingConfig} = useConfig();
  const { t } = useTranslation();

  const { 
    pet, 
    setPet, 
    updatePet, 
    resetPet,
    updatePetOwners,
    updatePetBirthday,
    updatePetDeath
  } = usePetStore();

  const {updatePetFromLocation} = location.state || {};

  const [owners, setOwners] = useState([]);
  const [races, setRaces] = useState([]);
  const [pelages, setPelages] = useState([]);
  const [genders, setGenders] = useState([]);
  const [characters, setCharacters] = useState([]);

  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({ original: null, avatar: null });
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showInlineCamera, setShowInlineCamera] = useState(false);
  const fileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [refs] = useState({
    owners: useRef(null),
    name: useRef(null),
    race: useRef(null),
    pelage: useRef(null),
    gender: useRef(null),
    character: useRef(null),
    birthday: useRef(null),
    weight: useRef(null),
    chip: useRef(null),
    notes: useRef(null),
  });

  useEffect(() => {
    
    if (updatePetFromLocation) {
      setPet(updatePetFromLocation);
    }

    if (location.state?.owner) {
      updatePet({
        owners: [location.state.owner]
      });
    }

    const currentThumbUrl = !photoDeleted ? (capturedPhoto?.url || pet?.thumb_url || '') : '';
    const petTypeName = config?.vet?.pet_naming_es ?? '';

    updateTitle(
      currentThumbUrl,
      isEditing(pet)
        ? t('edit.updatePet', { name: petTypeName })
        : t('edit.newPet', { name: petTypeName }),
      t('common.completeData'),
      false,
      !currentThumbUrl ? PhotoCamera : null,
      handleAvatarClick
    );
      
    toggleFab(false);

    const fetchData = async () => {
      try {
        const owners = await fetchOwners();
        const petsForInput = await fetchForInput('pets', 'pets_for_input');
        setOwners(owners);
        setRaces(petsForInput.pets_races);
        setPelages(petsForInput.pets_pelages);
        setGenders(petsForInput.pets_genders);
        setCharacters(petsForInput.pets_characters);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);

        // Add a delay to focus to ensure component is fully rendered
        setTimeout(() => {
          if (refs.name.current) {
            refs.name.current.focus();
          }
        }, 100);    
      }
    };

    fetchData();
  }, []);

  // Actualizar el header cuando cambia la foto capturada o el config
  useEffect(() => {
    const currentThumbUrl = !photoDeleted ? (capturedPhoto?.url || pet?.thumb_url || '') : '';
    const petTypeName = config?.vet?.pet_naming_es ?? '';

    updateTitle(
      currentThumbUrl,
      isEditing(pet)
        ? t('edit.updatePet', { name: petTypeName })
        : t('edit.newPet', { name: petTypeName }),
      t('common.completeData'),
      false,
      !currentThumbUrl ? PhotoCamera : null,
      handleAvatarClick
    );
  }, [capturedPhoto, pet?.id, config, photoDeleted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    updatePet({ [name]: value });
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCameraCapture = () => {
    handleMenuClose();
    setShowInlineCamera(true);
  };

  const handleGallerySelect = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemovePhoto = () => {
    handleMenuClose();
    setPhotoDeleted(true);
    setCapturedPhoto(null);
    setUploadedImages({ original: null, avatar: null });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const unique_id = uuidv4();
      const extension = file.name.split('.').pop();
      const newFileName = `${unique_id}.${extension}`;
      
      const renamedFile = new File([file], newFileName, { type: file.type });
      
      const processedPhoto = {
        file: renamedFile,
        url: URL.createObjectURL(file),
        name: newFileName,
        size: file.size,
        type: file.type,
        isImage: true,
        isVideo: false,
        sizeInMB: (file.size / 1024 / 1024).toFixed(2)
      };
      
      setCapturedPhoto(processedPhoto);
      setPhotoDeleted(false);
      await handlePhotoAndThumbUpload(renamedFile);
    }
    
    event.target.value = '';
  };

  const handleInlinePhotoConfirm = async (file, previewUrl) => {
    // Actualizar preview local
    setCapturedPhoto({
      url: previewUrl,
      file: file,
      isImage: true
    });

    setPhotoDeleted(false);
    // Subir la imagen cuando se confirma
    await handlePhotoAndThumbUpload(file);
  };

  // Función para manejar la subida de imágenes
  const handlePhotoAndThumbUpload = async (originalFile) => {
    try {
      setIsUploadingImage(true);

      const result = await uploadPhotoAndThumbnail({
        originalFile,
        tableName: 'pets',
        onError: (error) => {
          snackbar(t('common.errorUploadingImages') + error);
        }
      });

      // Actualizar estado con las imágenes subidas
      setUploadedImages({
        original: result.original,
        avatar: result.thumbnail
      });
      
      // Si se sube una nueva imagen, resetear el estado de eliminación
      setPhotoDeleted(false);
      
    } catch (error) {
      snackbar(t('common.errorUploadingImages') + (error.message || ''));
    } finally {
      setIsUploadingImage(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateItemSelected('owners', pet.owners, setErrors, refs.owners) ||
        !validateNonEmpty('name', pet.name, setErrors, refs.name)) {
      return;
    }

    save();
  };

  const save = async () => {
    setIsLoading(true);

    const petToSend = {
      ...pet,
      birthday: pet.birthday ? pet.birthday.format('YYYY-MM-DD') : null
    };

    petToSend.resources = [];
    if (uploadedImages.original && uploadedImages.avatar) {
      petToSend.resources = [
        {
          file_name: uploadedImages.original.file_name,
          table_name: "pets",
          type: "PUBLIC",
          unique_id: uploadedImages.original.unique_id
        },
        {
          file_name: uploadedImages.avatar.file_name,
          table_name: "pets", 
          type: "PUBLIC",
          unique_id: uploadedImages.avatar.unique_id
        }
      ];
    }

    if (photoDeleted) {
      petToSend.photo_deleted = 1;
    } 

    try {
      let response;
      if (isEditing(pet)) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}pets`,
          removeNullsAndEmptys(petToSend),
          { params: { update: true, id: pet.id }, withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}pets`,
          removeNullsAndEmptys(petToSend),
          { withCredentials: true }
        );
      }

      navigate('/main/view-pet', { state: { id: response.data.data.id, updateLastView: false } }, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = ['owners', 'name', 'race', 'pelage', 'gender', 'character', 'birthday', 'weight', 'chip', 'notes'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        // Last field - submit the form
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };
  
  return (
    <EditPage 
      onSubmit={handleSubmit} 
      isLoading={isLoading || isLoadingConfig || isUploadingImage}
    >
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCameraCapture}>
          <ListItemIcon>
            <PhotoCamera />
          </ListItemIcon>
          <ListItemText>{t('common.camera')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleGallerySelect}>
          <ListItemIcon>
            <PhotoLibrary />
          </ListItemIcon>
          <ListItemText>{t('common.gallery')}</ListItemText>
        </MenuItem>
        {(capturedPhoto || pet.photo_url || pet.thumb_url) && !photoDeleted && (
          <MenuItem onClick={handleRemovePhoto}>
            <ListItemIcon>
              <Delete />
            </ListItemIcon>
            <ListItemText>{t('common.remove')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileSelect}
      />

      <InlinePhotoCapture 
        open={showInlineCamera}
        onPhotoConfirm={handleInlinePhotoConfirm}
        onClose={() => setShowInlineCamera(false)}
      />

      <Autocomplete
        multiple
        fullWidth
        options={owners}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={pet.owners}
        onChange={(e, newValue) => updatePetOwners(newValue)}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <div>
              <div>{option.name}</div>
              <div style={{ fontSize: '0.8em', color: 'gray' }}>
                {option.phone || option.email || ''}
              </div>
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={config.vet.owner_naming_es_plural + " *"}
            name="owner"
            margin="normal"
            inputRef={refs.owners}
            onKeyDown={(e) => handleKeyDown(e, 'owners')}
            error={!!errors.owners}
            helperText={errors.owners}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      
      <TextField
        fullWidth
        margin="normal"
        label={t('common.nameStar')}
        name="name"
        value={pet.name}
        onChange={handleChange}
        inputRef={refs.name}
        error={!!errors.name}
        helperText={errors.name}
        onKeyDown={(e) => handleKeyDown(e, 'name')}
      />
      <Autocomplete
        fullWidth
        options={races}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={pet.race}
        onChange={(e, newValue) => updatePet({ race: newValue })}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={option.thumb_url}
                alt={option.name}
                sx={{ width: 36, height: 36 }}
              />
              {option.name}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.race')}
            name="race"
            margin="normal"
            inputRef={refs.race}
            onKeyDown={(e) => handleKeyDown(e, 'race')}
          />
        )}
      />
      <Autocomplete
        fullWidth
        options={pelages}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={pet.pelage}
        onChange={(e, newValue) => updatePet({ pelage: newValue })}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.pelage')}
            name="pelage"
            margin="normal"
            inputRef={refs.pelage}
            onKeyDown={(e) => handleKeyDown(e, 'pelage')}
          />
        )}
      />
      <Autocomplete
        fullWidth
        options={genders}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={pet.gender}
        onChange={(e, newValue) => updatePet({ gender: newValue })}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.gender')}
            name="gender"
            margin="normal"
            inputRef={refs.gender}
            onKeyDown={(e) => handleKeyDown(e, 'gender')}
          />
        )}
      />
      <Autocomplete
        fullWidth
        options={characters}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={pet.character}
        onChange={(e, newValue) => updatePet({ character: newValue })}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.character')}
            name="character"
            margin="normal"
            inputRef={refs.character}
            onKeyDown={(e) => handleKeyDown(e, 'character')}
          />
        )}
      />
      
      <LocalizationProvider dateAdapter={AdapterDayjs} sx={{ marginTop: 16 }}>
        <DatePicker
          label={t('common.birthday')}
          value={pet.birthday}
          onChange={(newValue) => updatePetBirthday(newValue)}
          slotProps={{ 
            textField: { 
              fullWidth: true,
              margin: "normal",
              inputRef: refs.birthday,
              onKeyDown: (e) => handleKeyDown(e, 'birthday')
            } 
          }}
          format="DD/MM/YYYY"
        />
      </LocalizationProvider>

      <TextField
        fullWidth
        margin="normal"
        label={t('common.weight')}
        name="weight"
        value={pet.weight}
        onChange={handleChange}
        type="number"
        inputRef={refs.weight}
        onKeyDown={(e) => handleKeyDown(e, 'weight')}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.chip')}
        name="chip"
        value={pet.chip}
        onChange={handleChange}
        inputRef={refs.chip}
        onKeyDown={(e) => handleKeyDown(e, 'chip')}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.notes')}
        name="notes"
        value={pet.notes}
        onChange={handleChange}
        multiline
        rows={4}
        inputRef={refs.notes}
        onKeyDown={(e) => handleKeyDown(e, 'notes')}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(pet.death)}
            onChange={(e) => updatePetDeath(e.target.checked)}
            name="death"
          />
        }
        label={t('edit.decease')}
      />
    </EditPage>
  );
};

export default EditPet;

